import { Flex } from '@ironhack/design-system2/components/flex';
import { SimpleGrid } from '@ironhack/design-system2/components/grid';
import { Skeleton } from '@ironhack/design-system2/components/skeleton';
import { Text } from '@ironhack/design-system2/components/text';
import { assoc, pick, pipe } from 'ramda';
import { type ReactElement } from 'react';

import {
  ArticleCard,
  type ArticleCardData,
} from '@/app/components/client/article-card';
import { type DatoListArticle } from '@/lib/datocms/types';

type Args = {
  articleButtonText: string;
  articles: DatoListArticle[];
  has24ArticlesPerPage: boolean;
};

export const formatArticles = ({
  articleButtonText,
  articles,
  has24ArticlesPerPage,
}: Args): ArticleCardData[] =>
  articles.map(
    pipe(
      pick([
        'author',
        'body',
        'coverImage',
        'publicationDate',
        'readingTime',
        'series',
        'slug',
        'subtitle',
        'title',
      ]),
      assoc('buttonText', articleButtonText),
      assoc('gaEvent', {
        eventAction: 'a',
        eventCategory: 's',
        eventLabel: 'l',
      }),
      assoc('size', [
        '2xl',
        null,
        null,
        null,
        has24ArticlesPerPage ? '3xl' : 'l',
      ])
    )
  );

type Props = {
  articles: DatoListArticle[];
  articlesPerPage: number;
  notFoundText: string;
};

export const ArticleGridPlaceholder = (props: Props): ReactElement => {
  const { articles, articlesPerPage, notFoundText } = props;
  const has24ArticlesPerPage = articlesPerPage === 24;

  const formattedArticles = formatArticles({
    // We are just using the Skeleton so we can leave this blank
    articleButtonText: '',
    articles,
    has24ArticlesPerPage,
  });

  return (
    <>
      <SimpleGrid
        columns={[1, null, 2, null, has24ArticlesPerPage ? 3 : 4]}
        justifyItems="center"
        spacing={[2, null, 3]}
      >
        {formattedArticles.length === 0 ? (
          <Skeleton gridColumn="span 4">
            <Text
              color="gray.500"
              gridColumn={['span 1', null, 'span 4']}
              textStyle={['xl', null, '3xl']}
            >
              {notFoundText}
            </Text>
          </Skeleton>
        ) : (
          formattedArticles.map((article) => (
            <Skeleton borderRadius="xl" key={article.slug}>
              <ArticleCard card={article} />
            </Skeleton>
          ))
        )}
      </SimpleGrid>

      {/* Pagination placeholder */}
      {formattedArticles.length > 0 && (
        <Flex gap={1} justify="center">
          <Skeleton borderRadius="4px" boxSize="30px" />
          <Skeleton borderRadius="4px" boxSize="30px" />
          <Skeleton borderRadius="4px" boxSize="30px" />
          <Skeleton borderRadius="4px" boxSize="30px" />
          <Skeleton borderRadius="4px" boxSize="30px" />
        </Flex>
      )}
    </>
  );
};
